import { useEffect, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Dialog } from '@factorialco/f0-react'
import type {
  FinanceEnumsQuoteTypeEnum,
  PreviewFinanceQuotePreviewLineItemsInput,
  PreviewQuoteMutationVariables,
} from 'generated/resources/graphql'
import {
  PreviewQuoteDocument,
  QuoteDetailsPageQuery,
  QuotePricingPageQuery,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'

import DocumentViewer from 'modules/finance/components/DocumentViewer'
import { handleResult } from 'modules/core/lib/handleMutation'
import handleResultWithFlash from 'modules/finance/lib/handleResultWithFlash'

const TIME_AND_MATERIALS_QUOTE_TYPE: FinanceEnumsQuoteTypeEnum = 'timeandmaterials'

type QuoteForPreview =
  | NonNullable<QuoteDetailsPageQuery['finance']['quote']>
  | NonNullable<QuotePricingPageQuery['finance']['quote']>

function isPhaseLineItem(item: { type?: string | null }): boolean {
  return item.type === 'phase'
}

function mapToPreviewLineItems(quote: QuoteForPreview): PreviewFinanceQuotePreviewLineItemsInput[] {
  const items = quote.financeLineItems ?? []
  const itemsToShow =
    quote.quoteType === TIME_AND_MATERIALS_QUOTE_TYPE
      ? items.filter(isPhaseLineItem)
      : items

  return itemsToShow.map((item) => ({
    description: item.description,
    quantity: isPhaseLineItem(item) ? 1 : item.quantity,
    amountCents: isPhaseLineItem(item)
      ? Number(item.basePriceCents)
      : Number(item.unitPriceCents),
    taxRateId: item.taxRateId ?? undefined,
  }))
}

function buildPreviewVariables(quote: QuoteForPreview): PreviewQuoteMutationVariables {
  return {
    currency: quote.currency ?? undefined,
    quoteNumber: quote.quoteNumber ?? undefined,
    quoteType: quote.quoteType,
    expirationDate: quote.expirationDate ?? undefined,
    documentDate: quote.documentDate ?? undefined,
    legalEntityId: quote.legalEntity?.id ?? undefined,
    clientId: quote.client?.id ?? undefined,
    lineItems: mapToPreviewLineItems(quote),
    discountPercentage: quote.discountPercentage ?? undefined,
    memo: quote.memo ?? undefined,
    footer: quote.footer ?? undefined,
  }
}

interface PreviewQuoteDialogProps {
  isOpen: boolean
  onClose: () => void
  quote: QuoteForPreview | null | undefined
}

export const PreviewQuoteDialog = ({ isOpen, onClose, quote }: PreviewQuoteDialogProps) => {
  const [previewHtml, setPreviewHtml] = useState<string | undefined>(undefined)

  const [previewQuote, { loading: previewLoading }] = useMutation(PreviewQuoteDocument, {
    extractorFn: (data) => data.financeMutations.previewFinanceQuotePreview,
  })

  useEffect(() => {
    if (!isOpen || !quote) {
      return
    }
    const variables = buildPreviewVariables(quote)
    previewQuote({ variables }).then((unhandledResult) => {
      const handled = handleResult(unhandledResult, handleResultWithFlash())
      if (handled.success && handled.data.quotePreview?.htmlContent) {
        setPreviewHtml(handled.data.quotePreview.htmlContent)
      } else {
        setPreviewHtml(undefined)
      }
    })
  }, [isOpen, quote, previewQuote])

  useEffect(() => {
    if (!isOpen) {
      setPreviewHtml(undefined)
    }
  }, [isOpen])

  return (
    <F0Dialog isOpen={isOpen} onClose={onClose} position="fullscreen">
      {previewLoading && (
        <Box padding="s16">
          <Text size="400">{'Loading preview…'}</Text>
        </Box>
      )}
      {!previewLoading && previewHtml && (
        <Box height="full">
          <DocumentViewer htmlContent={previewHtml} />
        </Box>
      )}
    </F0Dialog>
  )
}
